import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "../../lib/supabase";
import { Button } from "../../components/ui/button";
import {
  SidebarToggle,
  useAdminLayout,
} from "../../components/admin/AdminLayout";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../../components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui/table";
import { Input } from "../../components/ui/input";
import {
  Edit,
  Trash,
  Plus,
  Search,
  CheckCircle,
  XCircle,
  ShoppingBag,
  Tag,
  DollarSign,
  Store,
  Link as LinkIcon,
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../../components/ui/alert-dialog";
import { Label } from "../../components/ui/label";
import { useToast } from "../../hooks/use-toast";
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "../../components/ui/pagination";

type DealType = {
  id: string;
  title: string;
  image: string;
  original_price: string;
  sale_price: string;
  discount: string;
  store: string;
  link: string;
  active: boolean;
  created_at?: string;
  updated_at?: string;
};

type FormErrorType = {
  title?: string;
  image?: string;
  original_price?: string;
  sale_price?: string;
  discount?: string;
  store?: string;
  link?: string;
  [key: string]: string | undefined;
};

const AdminDeals = () => {
  const [deals, setDeals] = useState<DealType[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentDeal, setCurrentDeal] = useState<Partial<DealType>>({});
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [formErrors, setFormErrors] = useState<FormErrorType>({});
  const [savingDeal, setSavingDeal] = useState(false);
  const [deletingDeal, setDeletingDeal] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalDeals, setTotalDeals] = useState(0);
  const [totalPages, setTotalPages] = useState(1);

  const navigate = useNavigate();
  const { toast } = useToast();
  const { isMobile, setSidebarVisible } = useAdminLayout();

  useEffect(() => {
    loadDeals();
  }, [currentPage, pageSize]);

  useEffect(() => {
    return () => {
      if (imagePreview) {
        URL.revokeObjectURL(imagePreview);
      }
    };
  }, [imagePreview]);

  const loadDeals = async () => {
    setLoading(true);
    try {
      // Get total count first
      const { count, error: countError } = await supabase
        .from("deals")
        .select("*", { count: "exact", head: true });

      if (countError) throw countError;

      setTotalDeals(count || 0);
      setTotalPages(Math.ceil((count || 0) / pageSize));

      // Then get paginated data
      const from = (currentPage - 1) * pageSize;
      const to = from + pageSize - 1;

      const { data, error } = await supabase
        .from("deals")
        .select("*")
        .order("created_at", { ascending: false })
        .range(from, to);

      if (error) throw error;
      setDeals(data || []);
    } catch (error) {
      console.error("Error loading deals:", error);
      toast({
        title: "Lỗi tải dữ liệu",
        description: "Không thể tải danh sách deals",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    if (!searchTerm.trim()) {
      loadDeals();
      return;
    }

    // Reset to first page when searching
    setCurrentPage(1);

    // Local search for simplicity
    const filteredDeals = deals.filter(
      (deal) =>
        deal.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        deal.store.toLowerCase().includes(searchTerm.toLowerCase())
    );
    setDeals(filteredDeals);
  };

  const openEditDialog = (deal?: DealType) => {
    if (deal) {
      setCurrentDeal(deal);
      setImagePreview(deal.image);
    } else {
      setCurrentDeal({
        title: "",
        image: "",
        original_price: "",
        sale_price: "",
        discount: "",
        store: "",
        link: "",
        active: true,
      });
      setImagePreview(null);
    }
    setFormErrors({});
    setIsEditDialogOpen(true);
  };

  const openDeleteDialog = (deal: DealType) => {
    setCurrentDeal(deal);
    setIsDeleteDialogOpen(true);
  };

  const handleImageChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file
    if (!file.type.startsWith("image/")) {
      setUploadError("Vui lòng chọn file hình ảnh");
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      setUploadError("Kích thước file không được vượt quá 5MB");
      return;
    }

    // Clear previous errors
    setUploadError(null);

    // Create preview
    const preview = URL.createObjectURL(file);
    setImagePreview(preview);

    try {
      setUploadingImage(true);

      // Upload to Supabase Storage
      const fileName = `deals/${Date.now()}-${file.name.replace(/\s+/g, "-")}`;
      const { data, error } = await supabase.storage
        .from("images")
        .upload(fileName, file);

      if (error) throw error;

      // Get public URL
      const { data: urlData } = supabase.storage
        .from("images")
        .getPublicUrl(fileName);

      if (urlData) {
        setCurrentDeal({
          ...currentDeal,
          image: urlData.publicUrl,
        });
      }
    } catch (error) {
      console.error("Error uploading image:", error);
      setUploadError("Lỗi khi tải lên hình ảnh. Vui lòng thử lại.");
    } finally {
      setUploadingImage(false);
    }
  };

  const validateForm = () => {
    const errors: FormErrorType = {};

    if (!currentDeal.title) errors.title = "Tiêu đề không được để trống";
    if (!currentDeal.image) errors.image = "Hình ảnh không được để trống";
    if (!currentDeal.original_price)
      errors.original_price = "Giá gốc không được để trống";
    if (!currentDeal.sale_price)
      errors.sale_price = "Giá khuyến mãi không được để trống";
    if (!currentDeal.discount)
      errors.discount = "Phần trăm giảm giá không được để trống";
    if (!currentDeal.store) errors.store = "Cửa hàng không được để trống";
    if (!currentDeal.link) errors.link = "Link không được để trống";
    else if (!/^https?:\/\//.test(currentDeal.link))
      errors.link = "Link phải bắt đầu bằng http:// hoặc https://";

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSaveDeal = async () => {
    if (!validateForm()) return;

    setSavingDeal(true);
    const now = new Date().toISOString();

    try {
      if (currentDeal.id) {
        // Update existing deal
        const { error } = await supabase
          .from("deals")
          .update({
            title: currentDeal.title,
            image: currentDeal.image,
            original_price: currentDeal.original_price,
            sale_price: currentDeal.sale_price,
            discount: currentDeal.discount,
            store: currentDeal.store,
            link: currentDeal.link,
            active: currentDeal.active ?? true,
            updated_at: now,
          })
          .eq("id", currentDeal.id);

        if (error) throw error;

        toast({
          title: "Cập nhật thành công",
          description: "Deal đã được cập nhật.",
        });
      } else {
        // Create new deal
        const { error } = await supabase.from("deals").insert({
          title: currentDeal.title,
          image: currentDeal.image,
          original_price: currentDeal.original_price,
          sale_price: currentDeal.sale_price,
          discount: currentDeal.discount,
          store: currentDeal.store,
          link: currentDeal.link,
          active: currentDeal.active ?? true,
        });

        if (error) throw error;

        toast({
          title: "Tạo mới thành công",
          description: "Deal đã được tạo mới.",
        });
      }

      setIsEditDialogOpen(false);
      loadDeals();
    } catch (error) {
      console.error("Error saving deal:", error);
      toast({
        title: "Lỗi",
        description: "Không thể lưu deal. Vui lòng thử lại.",
        variant: "destructive",
      });
    } finally {
      setSavingDeal(false);
    }
  };

  const handleDeleteDeal = async () => {
    if (!currentDeal.id) return;

    setDeletingDeal(true);
    try {
      const { error } = await supabase
        .from("deals")
        .delete()
        .eq("id", currentDeal.id);

      if (error) throw error;

      toast({
        title: "Xóa thành công",
        description: "Deal đã được xóa.",
      });

      setIsDeleteDialogOpen(false);
      loadDeals();
    } catch (error) {
      console.error("Error deleting deal:", error);
      toast({
        title: "Lỗi",
        description: "Không thể xóa deal. Vui lòng thử lại.",
        variant: "destructive",
      });
    } finally {
      setDeletingDeal(false);
    }
  };

  const formatDate = (dateStr?: string) => {
    if (!dateStr) return "";
    return new Date(dateStr).toLocaleDateString("vi-VN", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  return (
    <>
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center">
          {isMobile && <SidebarToggle className="mr-2" />}
          <h1 className="text-2xl font-bold">Quản lý Deals</h1>
        </div>
        <Button onClick={() => openEditDialog()}>
          <Plus className="h-4 w-4 mr-2" />
          <span>Thêm mới</span>
        </Button>
      </div>

      <Card>
        <CardHeader>
          <div className="flex flex-col md:flex-row gap-4 justify-between items-start md:items-center">
            <CardTitle>Danh sách Deals</CardTitle>
            <div className="flex gap-2 w-full md:w-auto">
              <Input
                placeholder="Tìm kiếm..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="max-w-[300px]"
              />
              <Button variant="outline" onClick={handleSearch}>
                <Search className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="rounded-md border overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[80px]">Hình ảnh</TableHead>
                  <TableHead>Tiêu đề</TableHead>
                  <TableHead>Cửa hàng</TableHead>
                  <TableHead className="hidden md:table-cell">Giá</TableHead>
                  <TableHead className="hidden md:table-cell">
                    Ngày tạo
                  </TableHead>
                  <TableHead className="hidden md:table-cell">
                    Trạng thái
                  </TableHead>
                  <TableHead className="text-right w-[100px]">
                    Thao tác
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center py-10">
                      Đang tải...
                    </TableCell>
                  </TableRow>
                ) : deals.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center py-10">
                      Không có deals nào
                    </TableCell>
                  </TableRow>
                ) : (
                  deals.map((deal) => (
                    <TableRow key={deal.id}>
                      <TableCell>
                        <div className="w-12 h-12 rounded-md overflow-hidden">
                          <img
                            src={deal.image}
                            alt={deal.title}
                            className="w-full h-full object-cover"
                          />
                        </div>
                      </TableCell>
                      <TableCell className="font-medium">
                        {deal.title}
                      </TableCell>
                      <TableCell>{deal.store}</TableCell>
                      <TableCell className="hidden md:table-cell">
                        <div className="flex flex-col">
                          <span className="text-sm text-red-500 font-medium">
                            {deal.sale_price}
                          </span>
                          <span className="text-xs text-gray-500 line-through">
                            {deal.original_price}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell className="hidden md:table-cell">
                        {formatDate(deal.created_at)}
                      </TableCell>
                      <TableCell className="hidden md:table-cell">
                        {deal.active ? (
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                            <CheckCircle className="h-3 w-3 mr-1" />
                            Đang hiển thị
                          </span>
                        ) : (
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                            <XCircle className="h-3 w-3 mr-1" />
                            Ẩn
                          </span>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-2">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => openEditDialog(deal)}
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => openDeleteDialog(deal)}
                          >
                            <Trash className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <Pagination className="mt-4">
              <PaginationContent>
                <PaginationItem>
                  <PaginationPrevious
                    onClick={() =>
                      setCurrentPage((prev) => Math.max(prev - 1, 1))
                    }
                    disabled={currentPage === 1}
                    className={
                      currentPage === 1
                        ? "opacity-50 cursor-not-allowed"
                        : "cursor-pointer"
                    }
                  />
                </PaginationItem>
                {Array.from({ length: totalPages }).map((_, i) => (
                  <PaginationItem key={i}>
                    <PaginationLink
                      isActive={currentPage === i + 1}
                      onClick={() => setCurrentPage(i + 1)}
                      className="cursor-pointer"
                    >
                      {i + 1}
                    </PaginationLink>
                  </PaginationItem>
                ))}
                <PaginationItem>
                  <PaginationNext
                    onClick={() =>
                      setCurrentPage((prev) => Math.min(prev + 1, totalPages))
                    }
                    disabled={currentPage === totalPages}
                    className={
                      currentPage === totalPages
                        ? "opacity-50 cursor-not-allowed"
                        : "cursor-pointer"
                    }
                  />
                </PaginationItem>
              </PaginationContent>
            </Pagination>
          )}
        </CardContent>
      </Card>

      {/* Edit Dialog */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {currentDeal.id ? "Chỉnh sửa Deal" : "Thêm Deal mới"}
            </DialogTitle>
            <DialogDescription>
              Điền thông tin chi tiết cho deal.
            </DialogDescription>
          </DialogHeader>

          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Image Upload */}
              <div className="space-y-2">
                <Label htmlFor="image">Hình ảnh</Label>
                <div className="border border-dashed rounded-md p-4 text-center">
                  {imagePreview ? (
                    <div className="relative">
                      <img
                        src={imagePreview}
                        alt="Preview"
                        className="mx-auto h-40 object-contain"
                      />
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="mt-2"
                        onClick={() => {
                          setImagePreview(null);
                          setCurrentDeal({ ...currentDeal, image: "" });
                        }}
                      >
                        Xóa hình
                      </Button>
                    </div>
                  ) : (
                    <div className="py-4">
                      <div className="flex flex-col items-center">
                        <ShoppingBag className="h-10 w-10 text-gray-300 mb-2" />
                        <p className="text-sm text-gray-500">
                          Kéo thả hoặc nhấn để tải lên
                        </p>
                        <p className="text-xs text-gray-400 mt-1">
                          PNG, JPG hoặc WEBP (tối đa 5MB)
                        </p>
                      </div>
                      {uploadError && (
                        <p className="text-red-500 text-xs mt-2">
                          {uploadError}
                        </p>
                      )}
                    </div>
                  )}

                  <Input
                    id="image"
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={handleImageChange}
                    disabled={uploadingImage}
                  />
                  <Label
                    htmlFor="image"
                    className="block mt-2 text-sm text-center cursor-pointer"
                  >
                    {uploadingImage
                      ? "Đang tải lên..."
                      : "Nhấn để chọn hình ảnh"}
                  </Label>
                </div>
                {formErrors.image && (
                  <p className="text-red-500 text-xs">{formErrors.image}</p>
                )}
              </div>

              <div className="space-y-4">
                {/* Title */}
                <div className="space-y-2">
                  <Label htmlFor="title">Tiêu đề</Label>
                  <Input
                    id="title"
                    value={currentDeal.title || ""}
                    onChange={(e) =>
                      setCurrentDeal({ ...currentDeal, title: e.target.value })
                    }
                  />
                  {formErrors.title && (
                    <p className="text-red-500 text-xs">{formErrors.title}</p>
                  )}
                </div>

                {/* Store */}
                <div className="space-y-2">
                  <Label htmlFor="store">Cửa hàng</Label>
                  <div className="relative">
                    <Store className="absolute left-2 top-2.5 h-4 w-4 text-gray-500" />
                    <Input
                      id="store"
                      className="pl-8"
                      value={currentDeal.store || ""}
                      onChange={(e) =>
                        setCurrentDeal({
                          ...currentDeal,
                          store: e.target.value,
                        })
                      }
                    />
                  </div>
                  {formErrors.store && (
                    <p className="text-red-500 text-xs">{formErrors.store}</p>
                  )}
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* Original Price */}
              <div className="space-y-2">
                <Label htmlFor="original_price">Giá gốc</Label>
                <div className="relative">
                  <DollarSign className="absolute left-2 top-2.5 h-4 w-4 text-gray-500" />
                  <Input
                    id="original_price"
                    className="pl-8"
                    value={currentDeal.original_price || ""}
                    onChange={(e) =>
                      setCurrentDeal({
                        ...currentDeal,
                        original_price: e.target.value,
                      })
                    }
                    placeholder="$99.99"
                  />
                </div>
                {formErrors.original_price && (
                  <p className="text-red-500 text-xs">
                    {formErrors.original_price}
                  </p>
                )}
              </div>

              {/* Sale Price */}
              <div className="space-y-2">
                <Label htmlFor="sale_price">Giá khuyến mãi</Label>
                <div className="relative">
                  <DollarSign className="absolute left-2 top-2.5 h-4 w-4 text-gray-500" />
                  <Input
                    id="sale_price"
                    className="pl-8"
                    value={currentDeal.sale_price || ""}
                    onChange={(e) =>
                      setCurrentDeal({
                        ...currentDeal,
                        sale_price: e.target.value,
                      })
                    }
                    placeholder="$79.99"
                  />
                </div>
                {formErrors.sale_price && (
                  <p className="text-red-500 text-xs">
                    {formErrors.sale_price}
                  </p>
                )}
              </div>

              {/* Discount */}
              <div className="space-y-2">
                <Label htmlFor="discount">Giảm giá</Label>
                <div className="relative">
                  <Tag className="absolute left-2 top-2.5 h-4 w-4 text-gray-500" />
                  <Input
                    id="discount"
                    className="pl-8"
                    value={currentDeal.discount || ""}
                    onChange={(e) =>
                      setCurrentDeal({
                        ...currentDeal,
                        discount: e.target.value,
                      })
                    }
                    placeholder="20%"
                  />
                </div>
                {formErrors.discount && (
                  <p className="text-red-500 text-xs">{formErrors.discount}</p>
                )}
              </div>
            </div>

            {/* Link */}
            <div className="space-y-2">
              <Label htmlFor="link">Link</Label>
              <div className="relative">
                <LinkIcon className="absolute left-2 top-2.5 h-4 w-4 text-gray-500" />
                <Input
                  id="link"
                  className="pl-8"
                  value={currentDeal.link || ""}
                  onChange={(e) =>
                    setCurrentDeal({ ...currentDeal, link: e.target.value })
                  }
                  placeholder="https://example.com/product"
                />
              </div>
              {formErrors.link && (
                <p className="text-red-500 text-xs">{formErrors.link}</p>
              )}
            </div>

            {/* Active Status */}
            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                id="active"
                checked={currentDeal.active ?? true}
                onChange={(e) =>
                  setCurrentDeal({ ...currentDeal, active: e.target.checked })
                }
                className="rounded border-gray-300 text-primary focus:ring-primary"
              />
              <Label htmlFor="active" className="cursor-pointer">
                Hiển thị deal này
              </Label>
            </div>
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setIsEditDialogOpen(false)}
            >
              Hủy
            </Button>
            <Button
              type="button"
              onClick={handleSaveDeal}
              disabled={savingDeal}
            >
              {savingDeal ? "Đang lưu..." : "Lưu"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog
        open={isDeleteDialogOpen}
        onOpenChange={setIsDeleteDialogOpen}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Bạn có chắc muốn xóa?</AlertDialogTitle>
            <AlertDialogDescription>
              Thao tác này không thể hoàn tác. Deal sẽ bị xóa vĩnh viễn khỏi hệ
              thống.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Hủy</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteDeal}
              disabled={deletingDeal}
            >
              {deletingDeal ? "Đang xóa..." : "Xóa"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

export default AdminDeals;
